import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { useThemeStore } from '@/stores/theme.store';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { mobileMenuOpen, closeMobileMenu } = useThemeStore();
  const location = useLocation();

  // Auto-close mobile menu on navigation
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="flex h-screen bg-sime-bg">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-sime-accent focus:text-white focus:rounded-lg focus:m-2"
      >
        Saltar al contenido
      </a>

      {/* Desktop sidebar — collapsed (68px), expands on hover via absolute positioning */}
      <div className="hidden md:block relative z-sime-dropdown shrink-0 w-[68px]">
        <div className="absolute inset-y-0 left-0">
          <Sidebar />
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen ? (
        <div className="md:hidden fixed inset-0 z-sime-modal flex">
          <div
            className="absolute inset-0 bg-sime-overlay"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div className="relative z-10 w-[280px] max-w-[85vw]">
            <Sidebar mobile onClose={closeMobileMenu} />
          </div>
        </div>
      ) : null}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header />
        <main id="main-content" role="main" className="flex-1 overflow-y-auto p-3 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
